import type { SupabaseClient } from "@supabase/supabase-js";

// Rollout readiness read model — public.management_route_rollout_summary (one row per route;
// safe counts + blockers, no ids/coordinates). Defensive select(*) mapping. We don't .order() in
// the query (column-name-agnostic) and sort needs-attention-first client-side.

export interface RouteRolloutRow {
  routeId: string;
  routeName: string | null;
  siteId: string | null;
  siteName: string | null;
  clientName: string | null;
  routeActive: boolean;
  rolloutStatus: string;
  rolloutPercent: number | null;
  requiredCount: number;
  readyCount: number;
  needsSetupCount: number;
  missingMaterialCount: number;
  missingQrCount: number;
  missingNfcCount: number;
  missingGeofenceCount: number;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asInt(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : 0;
}

export function mapRouteRolloutRow(raw: Record<string, unknown>): RouteRolloutRow {
  return {
    routeId: asString(raw.route_id) ?? "",
    routeName: asString(raw.route_name),
    siteId: asString(raw.site_id),
    siteName: asString(raw.site_name),
    clientName: asString(raw.client_name),
    routeActive: raw.route_active === true,
    rolloutStatus: asString(raw.route_rollout_status) ?? asString(raw.rollout_status) ?? "unknown",
    rolloutPercent: asNumber(raw.rollout_percent),
    requiredCount: asInt(raw.required_checkpoint_count),
    readyCount: asInt(raw.ready_required_checkpoint_count),
    needsSetupCount: asInt(raw.needs_setup_required_checkpoint_count),
    missingMaterialCount: asInt(raw.missing_checkpoint_material_checkpoint_count),
    missingQrCount: asInt(raw.missing_qr_token_checkpoint_count),
    missingNfcCount: asInt(raw.missing_nfc_token_checkpoint_count),
    missingGeofenceCount: asInt(raw.missing_geofence_config_checkpoint_count),
  };
}

const ROLLOUT_LIMIT = 200;

/** Fetch route rollout readiness rows. Throws on read error (caller shows the error state). */
export async function fetchRouteRollout(
  client: SupabaseClient,
  accountId: string,
  limit: number = ROLLOUT_LIMIT,
): Promise<RouteRolloutRow[]> {
  const { data, error } = await client
    .from("management_route_rollout_summary")
    .select("*")
    .eq("account_id", accountId)
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load rollout status.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapRouteRolloutRow(row as Record<string, unknown>));
}
